// Sound engine using Web Audio API — no external files needed.
// Generates a soft piano-like ambient loop and a gentle chime for verses.

let ctx = null;
let masterGain = null;
let ambientNodes = null;
let ambientTimer = null;
let muted = null; // lazy-read

function getCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  masterGain = ctx.createGain();
  masterGain.gain.value = getMuted() ? 0 : 0.18;
  masterGain.connect(ctx.destination);
  return ctx;
}

export function getMuted() {
  if (muted !== null) return muted;
  try {
    muted = localStorage.getItem("rts_muted") === "1";
  } catch {
    muted = false;
  }
  return muted;
}

export function setMuted(v) {
  muted = Boolean(v);
  try {
    localStorage.setItem("rts_muted", muted ? "1" : "0");
  } catch {}
  if (masterGain && ctx) {
    masterGain.gain.cancelScheduledValues(ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(
      muted ? 0 : 0.18,
      ctx.currentTime + 0.35,
    );
  }
}

// Soft piano-ish tone: additive sines + slow amplitude envelope.
function playTone(freq, when, dur = 2.6, vol = 0.12) {
  if (!ctx) return;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vol, when + 0.08);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  g.connect(masterGain);

  const partials = [
    { m: 1, a: 1.0, type: "sine" },
    { m: 2, a: 0.35, type: "sine" },
    { m: 3, a: 0.16, type: "sine" },
    { m: 4, a: 0.08, type: "triangle" },
  ];
  for (const p of partials) {
    const o = ctx.createOscillator();
    o.type = p.type;
    o.frequency.value = freq * p.m;
    const pg = ctx.createGain();
    pg.gain.value = p.a;
    o.connect(pg).connect(g);
    o.start(when);
    o.stop(when + dur + 0.1);
  }
}

// Simple pentatonic scale, gentle sequence
const AMBIENT_NOTES_HZ = [
  220.0, // A3
  246.94, // B3
  277.18, // C#4
  329.63, // E4
  369.99, // F#4
  440.0, // A4
];

function scheduleAmbient() {
  if (!ctx) return;
  const now = ctx.currentTime;
  // Play 3 overlapping notes over ~6 seconds
  const positions = [0, 2.1, 4.3];
  for (const t of positions) {
    const f = AMBIENT_NOTES_HZ[Math.floor(Math.random() * AMBIENT_NOTES_HZ.length)];
    // Sometimes drop octave for depth
    const freq = Math.random() < 0.25 ? f / 2 : f;
    playTone(freq, now + t, 3.4, 0.09);
  }
}

export function startAmbient() {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume();
  if (ambientTimer) return;
  scheduleAmbient();
  ambientTimer = setInterval(scheduleAmbient, 6200);
}

export function stopAmbient() {
  if (ambientTimer) {
    clearInterval(ambientTimer);
    ambientTimer = null;
  }
}

// Gentle chime — 3-note arpeggio when a verse appears.
export function playChime() {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume();
  const now = c.currentTime;
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
  notes.forEach((f, i) => playTone(f, now + i * 0.16, 2.4, 0.14));
}

export function ensureContextResumed() {
  const c = getCtx();
  if (c && c.state === "suspended") c.resume();
}
